import React, { useEffect, useCallback, useState, useRef } from 'react';
import styles from './index.module.sass';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Components
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

// Queries
import Alert from '../Alert';
import PaymentIrelandPay from '../Queries/PaymentIrelandPay';
import SaleIrelandPay from '../Queries/SaleIrelandPay';

// Images
import MasterCard from '../../images/methods/master-cart.svg';
import Visa from '../../images/methods/visa.svg';
import Amex from '../../images/methods/amex.svg';

export default function Payment({ device, setSessionMessage, ostype = '', history }) {
  const { t } = useTranslation('payment');
  const { screenSize } = device;

  const [loading, setLoading] = useState(false);
  const { irelandPayTransactionToken = '' } = useParams();
  const [alertText, setAlertText] = useState('');
  const [open, setOpen] = useState(false);
  const [titleAlert, settitleAlert] = useState('Terramar Payment');

  const [paymentForm, setPaymentForm] = useState({
    name: '',
    lastName: '',
    address: '',
    state: '',
    city: '',
    cp: '',
    amount: 0,
    orderNumber: '',
    noKit: '',
  });

  const [irelandPayData, setIrelandPayData] = useState({
    ccnumber: false,
    ccexp: false,
    cvv: false,
  });

  function handleChange(e) {
    setPaymentForm({
      ...paymentForm,
      [e.target.name]: e.target.value,
    });
  }
  const finishSubmit = useCallback(
    async (response) => {
      const irelandPayToken = response?.token || '';
      const saleResponse = await SaleIrelandPay({
        jsonBody: {
          order_token: irelandPayTransactionToken,
          payment_token: irelandPayToken,
          address: paymentForm.address,
          city: paymentForm.city,
          state: paymentForm.state,
          zip: paymentForm.cp,
          idioma: 1,
        },
      });
      // /SaleIrelandPay?order_token={porder_token}&payment_token={ppayment_token}&idioma={pIdioma}
      const {
        message,
        typeMessage = 'error',
        responsetext = 'ERROR',
        response_code = '',
        NewOrderNumber = '',
        // messageCode = '',
        description = '',
        detail = '',
      } = saleResponse;
      setSessionMessage(message);
      if (typeMessage === 'success' && response_code === '100') {
        if (ostype === 'android') {
          // history.push('/ticketredirectandroid');
          window.open('https://terramarbrands.mx/redirectapp/android/', '_self');
        } else if (ostype === 'ios') {
          // history.push('/ticketredirectios');
          window.open('https://terramarbrands.mx/redirectapp/ios/', '_self');
        } else if (ostype === 'react') {
          window.open('https://terramarbrands.mx/redirectapp/react/', '_self');
        } else {
          if (NewOrderNumber) {
            const now = new Date();
            const time = now.getTime();
            const expireTime = time + 1000 * 36000;
            now.setTime(expireTime);
            document.cookie = `folioPedido=${NewOrderNumber};expires=${now.toUTCString()};path=/`;
          }
          setLoading(false);
          history.push(`/order/ticket/${irelandPayTransactionToken}`);
        }
      } else {
        // setAlertText(
        //   `the transaction was not successful, please try again. Code(${messageCode}), ${description}, ${detail} CodeZ(${response_code})`,
        // );
        setAlertText(
          `the transaction was not successful, please try again. ${description}, ${detail}`,
        );
        settitleAlert(responsetext);
        setOpen(true);
        setLoading(false);
      }
    },
    [
      irelandPayTransactionToken,
      setSessionMessage,
      ostype,
      history,
      paymentForm.address,
      paymentForm.city,
      paymentForm.state,
      paymentForm.cp,
    ],
  );

  async function handleSubmit(event) {
    event.preventDefault();
    const { ccnumber = '', ccexp = '', cvv = '' } = irelandPayData;

    if (ccnumber && ccexp && cvv) {
      setLoading(true);
      window.CollectJS.startPaymentRequest();
    } else {
      setAlertText('All data are required');
      settitleAlert('Terramar Payment ');
      setOpen(true);
    }
  }

  const handleFinishSubmitRef = useRef(finishSubmit);

  // cada render actualiza la ref con la versión más reciente de finishSubmit
  useEffect(() => {
    handleFinishSubmitRef.current = finishSubmit;
  }, [finishSubmit]);

  const configureCollectJS = useCallback(async () => {
    await window.CollectJS.configure({
      variant: 'inline',
      styleSniffer: 'false',
      callback: (token) => {
        handleFinishSubmitRef.current(token);
      },
      validationCallback: function (field, status, message) {
        setIrelandPayData((oldValue) => {
          return {
            ...oldValue,
            [field]: status,
          };
        });
      },
      googleFont: 'Poppins',
      customCss: {
        'background-color': '#ffffff',
        border: '2px solid #9b722a',
        padding: '10px 8px',
        'border-radius': '8px',
        'font-size': '16px',
        'outline-width': '0',
      },
      invalidCss: {
        color: '#f53a1a',
        border: '2px solid #f53a1a',
      },
      validCss: {
        color: 'green',
        'background-color': '#ffffff',
      },
      focusCss: {
        color: '#000000',
        'background-color': '#ffffff',
        border: '2px solid #002747',
      },
      fields: {
        ccnumber: {
          selector: '#ccnumber',
          title: 'Card Number',
          placeholder: '0000 0000 0000 0000',
        },
        ccexp: {
          selector: '#ccexp',
          title: 'Card Expiration',
          placeholder: '00 / 00',
        },
        cvv: {
          display: 'show',
          selector: '#cvv',
          title: 'CVV Code',
          placeholder: '***',
        },
      },
    });
  }, []);

  const getOrderData = useCallback(async () => {
    setLoading(true);
    const orderDataToken = await PaymentIrelandPay({
      jsonBody: {
        token: irelandPayTransactionToken,
        idioma: 1,
      },
    });

    const {
      message = '',
      amount = 0,
      orderNumber = '',
      typeMessage = 'error',
      noKit,
      DataConsultoriaBillingAddress = {},
    } = orderDataToken;

    const {
      CP = '',
      nombre = '',
      apellido = '',
      ciudad = '',
      estado = '',
      line1 = '',
    } = DataConsultoriaBillingAddress;

    setSessionMessage(message);

    if (typeMessage === 'success') {
      setPaymentForm({
        name: nombre,
        lastName: apellido,
        address: line1,
        state: estado,
        city: ciudad,
        cp: CP,
        amount,
        orderNumber,
        noKit,
      });
    }

    setLoading(false);
  }, [irelandPayTransactionToken, setSessionMessage]);

  useEffect(() => {
    window.scrollTo(0, 0);
    configureCollectJS();
    getOrderData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`${styles.container} ${styles[screenSize]}`}>
      <Alert open={open} setOpen={setOpen} title={titleAlert} text={alertText} />
      <div className={styles.main}>
        <div className={styles.formHeader}>
          <div className={styles.headerTitle}>
            {t('order')}: {paymentForm.orderNumber}
          </div>
          <div className={styles.headerTitle}>
            {t('id')}: {paymentForm.noKit}
          </div>
        </div>
        {paymentForm.amount && (
          <div className={styles.amountContainer}>
            <span>
              ${paymentForm.amount} <div className={styles.cardsIcons}></div>
            </span>
            <div className={styles.cardsIcons}>
              <img className={styles.cardIcon} src={MasterCard} alt='master card icon' />
              <img className={styles.cardIcon} src={Visa} alt='visa icon' />
              <img className={styles.cardIcon} src={Amex} alt='amex icon' />
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className={styles.inputContainer}>
            <div className={styles.paymentLabel}>{t('first')}</div>
            <input
              type='text'
              id='first_name'
              name='name'
              onChange={(e) => handleChange(e)}
              className={styles.paymentInput}
              value={paymentForm.name}
              readOnly='readonly'
            />
          </div>
          <div className={styles.inputContainer}>
            <div className={styles.paymentLabel}>{t('last')}</div>
            <input
              type='text'
              id='last_name'
              name='lastName'
              onChange={(e) => handleChange(e)}
              className={styles.paymentInput}
              value={paymentForm.lastName}
              readOnly='readonly'
            />
          </div>
          <div className={styles.inputContainer}>
            <div className={styles.paymentLabel}>{t('address')}</div>
            <input
              type='text'
              id='address'
              name='address'
              onChange={(e) => handleChange(e)}
              className={styles.paymentInput}
              value={paymentForm.address || ''}
              // readOnly='readonly'
            />
          </div>
          <div className={styles.inputContainer}>
            <div className={styles.paymentLabel}>{t('city')}</div>
            <input
              type='text'
              id='city'
              name='city'
              onChange={(e) => handleChange(e)}
              className={styles.paymentInput}
              value={paymentForm.city || ''}
              // readOnly='readonly'
            />
          </div>
          <div className={styles.inputContainer}>
            <div className={styles.paymentLabel}>{t('state')}</div>
            <input
              type='text'
              id='state'
              name='state'
              onChange={(e) => handleChange(e)}
              className={styles.paymentInput}
              value={paymentForm.state || ''}
              // readOnly='readonly'
            />
          </div>
          <div className={styles.inputContainer}>
            <div className={styles.paymentLabel}>{t('cp')}</div>
            <input
              type='text'
              id='cp'
              name='cp'
              onChange={(e) => handleChange(e)}
              className={styles.paymentInput}
              value={paymentForm.cp || ''}
              // readOnly='readonly'
            />
          </div>
          <div className={styles.inputContainer}>
            <div className={styles.paymentLabel}>{t('cardNumber')}</div>
            <div id='ccnumber' className={styles.paymentField} />
          </div>
          <div className={styles.inputContainer}>
            <div className={styles.paymentLabel}>{t('expirationDate')}</div>
            <div id='ccexp' className={styles.paymentField} />
          </div>
          <div className={styles.inputContainer}>
            <div className={styles.paymentLabel}>{t('cvv')}</div>
            <div id='cvv' className={styles.paymentField} />
          </div>

          <div className={styles.acctionButton}>
            <Button
              type='submit'
              variant='contained'
              size='large'
              color='primary'
              className={`${styles.button} ${styles.cancel}`}
              onClick={(e) => {
                if (ostype === 'android')
                  window.open('https://terramarbrands.mx/redirectapp/android/', '_self');
                else if (ostype === 'ios')
                  window.open('https://terramarbrands.mx/redirectapp/ios/', '_self');
                else if (ostype === 'react')
                  window.open('https://terramarbrands.mx/redirectapp/react/', '_self');
                else history.push('/order');
              }}>
              {t('cancel')}
            </Button>
            <Button
              type='submit'
              variant='contained'
              size='large'
              color='primary'
              className={styles.button}
              onClick={(e) => {
                if (!loading) handleSubmit(e);
              }}>
              {(loading && <CircularProgress className={styles.finishLoading} />) || (
                <div className={styles.paymentText}>
                  {t('makePayment')}
                  <i className={`material-icons ${styles.buttonIcon}`}>trending_flat</i>
                </div>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
